import { useRouter } from '@townsquare/router/useRouter';
import { PreviewPanelsProvider, useCurrentPreviewPanel, usePreviewPanelsActions } from '@atlassian/preview-panels-api';
import React, { useEffect, useRef } from 'react';

import { useRightSidebarActions, useRightSidebarState } from '@townsquare/sidebar-panel/right-sidebar';
import { fg } from '@townsquare/stat-sig/gate';
import { type GateNames } from '@townsquare/stat-sig/gate-keys';

/**
 * Coordinates the preview panel and RightSidebar systems so that only one is
 * visible at a time. Must be rendered as a child of PreviewPanelsProvider (so
 * that usePreviewPanelsActions is available).
 * Requires react-resource-router context for route-closing logic.
 */
const PreviewPanelSidebarCoordinator = ({ children }: { children: React.ReactNode }) => {
	const { currentPanel: currentRightSidebarPanel, id } = useRightSidebarState();
	const { close: closePreviewPanel } = usePreviewPanelsActions();
	const currentPreviewPanel = useCurrentPreviewPanel();
	const [routerStore] = useRouter();
	const routeKey = `${routerStore.location.pathname}${routerStore.location.search}`;
	const isFirstMount = useRef(true);

	// Close the preview panel when the RightSidebar opens
	useEffect(() => {
		if (currentRightSidebarPanel && currentPreviewPanel) {
			closePreviewPanel();
		}
	}, [currentRightSidebarPanel, id, closePreviewPanel, currentPreviewPanel]);

	// Close the preview panel when the route changes
	useEffect(() => {
		if (isFirstMount.current) {
			isFirstMount.current = false;
			return;
		}
		closePreviewPanel();
	}, [routeKey, closePreviewPanel]);

	return <>{children}</>;
};

type Props = {
	children: React.ReactNode;
	/**
	 * The feature gate to check before enabling preview panels.
	 * When the gate is off, children are rendered as-is with no preview panel context.
	 */
	featureGate: GateNames;
};

/**
 * A reusable provider that wires up the @atlassian/preview-panels-api PreviewPanelsProvider
 * with the Townsquare RightSidebar, ensuring only one side panel is visible at a time.
 *
 * Note - this is intended to be temporary, eventually we just want to use
 * @atlassian/preview-panels-api directly.
 *
 * Gated behind the provided feature gate.
 * Place inside `CapabilityRouter` so route changes close the preview panel.
 */
export const TownsquarePreviewPanelsProvider = ({ children, featureGate }: Props) => {
	if (!fg(featureGate)) {
		return <>{children}</>;
	}

	return <TownsquarePreviewPanelsProviderInner>{children}</TownsquarePreviewPanelsProviderInner>;
};

const TownsquarePreviewPanelsProviderInner = ({ children }: { children: React.ReactNode }) => {
	const { closePanel: closeRightSidebar } = useRightSidebarActions();

	return (
		<PreviewPanelsProvider
			parentProduct="townsquare"
			onPreviewPanelOpen={() => {
				// Avoid multiple right-side panels being visible at once.
				closeRightSidebar();
			}}
		>
			<PreviewPanelSidebarCoordinator>{children}</PreviewPanelSidebarCoordinator>
		</PreviewPanelsProvider>
	);
};
